<template>
  <div>
    <gg-card>
      <gg-card-header>
        <v-row>
          <v-col md="6">
            {{ title }}
          </v-col>
          <v-col md="6" class="d-flex flex-sm-row flex-column justify-end align-start gap-sm align-md-center">
            <slot name="header-actions" />
          </v-col>
        </v-row>
      </gg-card-header>
      <gg-card-title v-if="newAccount">
        <v-row>
          <v-col cols="12" sm="8" md="8">
            <add-new-account />
          </v-col>
        </v-row>
      </gg-card-title>
      <v-card-text>
        <v-data-table
          :headers="tableHeaders"
          :items="filteredItems"
          item-key="virtualId"
          :search="search"
          :footer-props="{ itemsPerPageOptions: itemsPerPageOptions }"
          sort-by="domain"
          sort-asc
        >
          <template v-slot:item.domain="{ item }">
            <div>
              <router-link :to="manageServiceLink(item)" custom v-slot="{ navigate }">
                <button
                  @click="navigate"
                  @keypress.enter="navigate"
                  role="link"
                  aria-label="Manage"
                  class="ggs-item-title primary--text">
                  {{ item.domain }}
                </button>
              </router-link>
              <template v-if="!hideIcons">
                <router-link v-if="item.sharedaccount || item.sharedaccess" :to="{ name: 'profile_access' }" class="pl-1">
                  <v-icon small>mdi-account-multiple</v-icon>
                </router-link>
              </template>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <router-link
                    v-if="item.cancellation_pending"
                    :to="`/cancellations/${item.cancellation_id}`"
                    aria-label="View Request"
                    class="pl-1"

                  >
                    <v-icon color="orange" small v-on="on" v-bind="attrs">mdi-alert-outline</v-icon>
                  </router-link>
                </template>
                <div>This service has a pending cancellation.</div>
                <div>Click to view/update your request.</div>
              </v-tooltip>
              <br>
              <v-icon v-show="item.flag" class="mr-1 caption">
                {{ iconFlag(item.flag) }}
              </v-icon>
              <span class="caption">{{ item.name }}</span>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <div>
              <v-icon small class="pb-1" :color="statusColor(item.status)">mdi-circle</v-icon>
              <span class="pl-1">{{ item.status }}</span>
            </div>
          </template>
          <template v-slot:item.renewaldate="{ item }">
            <div class="hidden-xs-only">
              {{ formatRenewalDate(parseRenewalDate(item.renewaldate)) }}
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <div>
              <gg-btn
                class="mr-1"
                :to="manageServiceLink(item)"
              >
                Manage
              </gg-btn>
              <gg-btn
                v-if="item.type === SERVICE_TYPES.CPANEL"
                class="mr-1"
                :to="`/hosting/shared/${item.id}/email-accounts`"
                secondary
              >
                Email
              </gg-btn>
              <template v-if="isSimplified && item.builders.length">
                <gg-btn
                  class="mr-1"
                  @click="manageBuilderLink(item)"
                  secondary
                >
                  <span v-if="item.builders.length === 1">
                    <span>{{ item.builders.at(0).name }}</span>
                    <v-icon small class='mb-1 ml-1'>mdi-open-in-new</v-icon>
                  </span>
                  <span v-else>Builders</span>
                </gg-btn>
              </template>
              <gg-btn
                v-if="hasServiceWP(item.id)"
                class="mr-1"
                :to="`/hosting/shared/${item.id}/wordpress/install-and-manage`"
                secondary
              >
                WordPress
              </gg-btn>
              <gg-btn
                v-if="item.type === SERVICE_TYPES.CPANEL"
                text
                class="mr-1 text-none"
                @click="launch_cpanel(item.id)"
              >
                cPanel
              </gg-btn>
              <gg-btn
                v-if="item.type === SERVICE_TYPES.RESELLER"
                text
                class="mr-1"
                @click="launch_whm(item.id)"
              >
                WHM
              </gg-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </gg-card>
  </div>
</template>

<script>
import {
  parseRenewalDate,
  formatRenewalDate,
  manageServiceLink,
  iconFlag,
  statusColor,
  launch_cpanel,
  launch_whm,
  getServiceRouteType,
} from 'Helpers/helpers'
import { mapActions, mapGetters } from 'vuex'
import AddNewAccount from 'Components/Services/Hosting/ServicesPage/AddNewAccount'
import { SERVICE_TYPES } from '@/consts/service'

export default {
  name: 'HostingServicesTable',
  components: {
    AddNewAccount,
  },
  props: {
    title: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true,
      default: () => ([])
    },
    filters: {
      type: Array,
      default: () => ([]),
    },
    search: {
      type: String,
      default: ''
    },
    newAccount: {
      type: Boolean,
      default: false
    },
    hideIcons: {
      type: Boolean,
      default: false
    },
    useBillingPerPageOptions: {
      type: Boolean,
      default: false,
    },
    isSimplified: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      table: {
        headers: [
          {
            text: 'Service',
            value: 'domain',
          },
          {
            text: 'Service Type',
            value: 'groupname',
          },
          {
            text: 'Status',
            value: 'status',
          },
          {
            text: 'Renewal Date',
            value: 'renewaldate',
            'class': 'hidden-xs-only',
          },
          {
            text: 'Actions',
            value: 'actions',
          }
        ],
      },
      filteredItems: [],
      simplifiedExcludedHeaders: ['renewaldate', 'groupname'],
    }
  },
  computed: {
    ...mapGetters(['pagination', 'paginationBilling', 'getWebsites']),
    itemsPerPageOptions () {
      return this.useBillingPerPageOptions ? this.paginationBilling : this.pagination
    },
    tableHeaders () {
      if (this.isSimplified) {
        return this.table.headers.filter(header => !this.simplifiedExcludedHeaders.includes(header.value))
      }
      return this.table.headers
    },
    SERVICE_TYPES () {
      return SERVICE_TYPES
    },
  },
  methods: {
    ...mapActions({
      navigateTo: 'navigateTo',
      sitebuilderLogin: 'hostingsitebuilder/sitebuilderLogin'
    }),
    manageServiceLink,
    parseRenewalDate,
    formatRenewalDate,
    iconFlag,
    statusColor,
    launch_cpanel,
    launch_whm,
    applyFilters (filters) {
      if (!filters || !filters.length) {
        this.filteredItems = this.items
        return
      }
      this.filteredItems = this.items.filter((service) => {
        return filters.includes(service.type)
      })
    },
    hasServiceWP (serviceId) {
      return this.getWebsites.findIndex((site) => parseInt(serviceId) === site.service_id && site.wp_insid) !== -1
    },
    manageBuilderLink (service) {
      if (service.builders.length === 1) {
        this.sitebuilderLogin(service.builders.at(0).id)
        return
      }
      const path = `hosting/${getServiceRouteType(service)}/${service.id}/sitebuilder`
      this.$router.push({ path })
    },
  },
  mounted () {
    this.applyFilters(this.filters)
  },
  watch: {
    filters: {
      handler (val) {
        this.applyFilters(val)
      },
      deep: true
    },
    items: {
      handler () {
        this.applyFilters(this.filters)
      },
      deep: true
    }
  },
}
</script>
